import '../../scrollytelling.css'
import './morph.css'

import PropTypes from 'prop-types'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { SceneProvider, useFrame } from '../../engine'
import { readParam } from '../i18n'
import { useShowStory } from '../showcase/stage/engine'
import { createMorph } from './runtime'
import { HIRE_CONTENTS, HIRE_IDS, buildHire } from './story/sceneHire'
import { NEED_CONTENTS, NEED_IDS, buildNeed } from './story/sceneNeed'
import { PLAY_CONTENTS, PLAY_IDS, buildPlay } from './story/scenePlay'
import * as SCRIPT from './story/script'

// Scrollstrecke je Sekunde Timeline
const VH_PER_SEC = 38

const CONTENTS = {
  ...NEED_CONTENTS,
  ...PLAY_CONTENTS,
  ...HIRE_CONTENTS
}
const SCENES = [
  { ids: NEED_IDS, build: buildNeed },
  { ids: PLAY_IDS, build: buildPlay },
  { ids: HIRE_IDS, build: buildHire }
]

/** Die eine Form mit allen Inhalten, Styles setzt `seek(t)`. */
function Stage() {
  return (
    <div className='mo-world' data-mo='world'>
      <div className='mo-shape' data-mo='shape'>
        {Object.entries(CONTENTS).map(([id, Content]) => (
          <div key={id} className='mo-c' data-c={id}>
            <Content />
          </div>
        ))}
      </div>
      <div className='mo-cursor' data-mo='cursor'>
        <svg width='26' height='30' viewBox='0 0 26 30' aria-hidden='true'>
          <path
            d='M2 2l0 22.5 6.2-5.6 4.1 9.1 4.2-1.9-4.1-9 8.3-.4Z'
            fill='#111111'
            stroke='#FFFFFF'
            strokeWidth='1.75'
            strokeLinejoin='round'
          />
        </svg>
      </div>
    </div>
  )
}

// Fira Sans liegt lokal im Dashboard, vor dem ersten Bild sicher laden
function useFonts() {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    let cancelled = false
    Promise.all(
      ['400', '500', '600'].map((w) =>
        document.fonts?.load(`${w} 16px "Fira Sans"`)
      )
    )
      .catch(() => {})
      .finally(() => !cancelled && setReady(true))
    return () => {
      cancelled = true
    }
  }, [])
  return ready
}

/** Scroll-Variante: die Timeline liegt auf der Scrollachse. */
function ScrollScene({ seekRef }) {
  useFrame((l) => seekRef.current?.(l * SCRIPT.DUR * 0.9999))
  return null
}

ScrollScene.propTypes = { seekRef: PropTypes.object.isRequired }

/**
 * Version „Aivy Morph“: eine Form, die nie geschnitten wird und eine Situation
 * erzählt, vom Team, das jemanden braucht, bis zur Einstellung.
 * `&play=1` spielt den Loop in Echtzeit, `&render=1` stellt eine quadratische
 * Bühne mit `window.__seek` fürs Video bereit.
 */
export default function AivyMorph() {
  const mode = readParam('render')
    ? 'render'
    : readParam('play')
      ? 'play'
      : 'scroll'
  const ready = useFonts()
  const stageRef = useRef(null)
  const seekRef = useRef(null)
  const story = useShowStory(1)

  useLayoutEffect(() => {
    if (!ready || !stageRef.current) return undefined
    const render = mode === 'render'
    const seek = createMorph(stageRef.current, SCRIPT, SCENES, {
      cursorScale: render ? 1.7 : 1,
      // Auf schmalen Bildschirmen begrenzt die Breite, dort darf die Form mehr füllen
      fill: render ? 0.7 : (W) => (W < 720 ? 0.88 : 0.64),
      cap: render ? 3 : 2.6
    })
    seekRef.current = seek
    seek(0)
    const dispose = () => {
      seek.dispose()
      seekRef.current = null
    }
    if (render) {
      window.__seek = seek
      window.__duration = SCRIPT.DUR
      window.__ready = true
      return () => {
        dispose()
        delete window.__seek
        window.__ready = false
      }
    }
    if (mode === 'play') {
      let raf = 0
      const start = performance.now()
      const loop = (now) => {
        seek((now - start) / 1000)
        raf = requestAnimationFrame(loop)
      }
      raf = requestAnimationFrame(loop)
      return () => {
        cancelAnimationFrame(raf)
        dispose()
      }
    }
    return dispose
  }, [ready, mode])

  if (mode !== 'scroll') {
    return (
      <div className={`sty-root mo-root mo-root--${mode}`}>
        <div ref={stageRef} className='mo-stage'>
          {ready && <Stage />}
        </div>
      </div>
    )
  }

  return (
    <div ref={story.storyRef} className='sty-root mo-root'>
      <div ref={story.progressRef} className='mo-progress' />
      <section
        ref={(el) => (story.chapterRefs.current[0] = el)}
        style={{ height: `${SCRIPT.DUR * VH_PER_SEC + 100}vh` }}
      >
        <div
          ref={(el) => (story.sceneRefs.current[0] = el)}
          className='mo-sticky'
        >
          <div ref={stageRef} className='mo-stage'>
            {ready && <Stage />}
          </div>
          <div className='mo-caption'>
            <strong>Vom Bedarf bis zur Einstellung.</strong>
            <span>Scroll, oder</span>
            <a href='?v=aivy-morph&play=1'>als Loop abspielen</a>
          </div>
          <SceneProvider index={0} subscribe={story.subscribe}>
            <ScrollScene seekRef={seekRef} />
          </SceneProvider>
        </div>
      </section>
    </div>
  )
}
